import {
  WSFilterApiOptionsLoader,
  WSFilterOption
} from "@wingspanhq/fe-component-library";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import { useMemo, useState } from "react";
import { useQueryPayerRows } from "../../../query/search/payer/queries/useQueryPayerRows";
import { PayerRowsQuery } from "../../../services/search";

/**
 * Custom hook for providing API-based payer filter options
 * with pagination and search support.
 *
 * @returns WSFilterApiOptionsLoader configuration for WSFilters
 *
 * @example
 * const payerOptions = usePayerFilterOptions();
 *
 * const filters: WSFilter[] = [
 *   {
 *     title: "Client",
 *     name: "payerId",
 *     type: "select",
 *     apiOptions: payerOptions
 *   }
 * ];
 */
export function usePayerFilterOptions(
  baseQuery?: PayerRowsQuery
): WSFilterApiOptionsLoader {
  const [searchText, setSearchText] = useState("");

  const query = useQueryPayerRows({
    ...baseQuery,
    filter: {
      ...baseQuery?.filter,
      searchString: searchText || undefined
    }
  });

  const options = useMemo(() => {
    return (query.data || [])
      .map(payer => {
        if (!payer.user) return null;

        const payerNames = wsName({
          user: payer.user,
          member: payer.member,
          payeeOwnedData: payer.payeeOwnedData
        });

        const label = payerNames.getResolvedName();
        const helperText = payerNames.getResolvedSecondaryName();

        return {
          name: payer.payerId,
          label,
          helperText
        };
      })
      .filter(Boolean) as WSFilterOption<"checkbox">[];
  }, [query.data]);

  return {
    data: options,
    isLoading: query.isInitialLoading,
    pagination: {
      hasNextPage: query.hasNextPage ?? false,
      isFetching: query.isFetching,
      isFetchingNextPage: query.isFetchingNextPage,
      fetchNextPage: query.fetchNextPage
    },
    search: {
      searchText,
      onSearchChange: setSearchText
    }
  };
}
